// Batch TMDB Data Loader
// Efficiently loads TMDB data for multiple movies with intelligent batching

import { getCachedTMDBData, setCachedTMDBData, getCacheKey } from '../services/tmdbCache';
import { findBestMovieMatch, extractMovieInfo, tmdbIdOf } from '../services/tmdbService';

class BatchTMDBLoader {
  constructor() {
    this.pendingRequests = new Map(); // movieKey -> Promise
    this.batchQueue = [];
    this.batchTimeout = null;
    this.batchDelay = 100; // ms to wait before processing batch
    this.maxBatchSize = 10;
    this.apiDelay = 250; // ms between API calls to respect rate limits
  }

  // Get a unique key for a movie: the TMDB id when known, title:year otherwise.
  // The same key the cache uses, so the two can never disagree.
  getMovieKey(title, year, tmdbId = null) {
    return getCacheKey(title, year, tmdbId);
  }

  // Get TMDB data for a movie (with batching)
  async getMovieData(title, year, tmdbId = null) {
    const movieKey = this.getMovieKey(title, year, tmdbId);

    // If there's already a pending request for this movie, return that promise
    if (this.pendingRequests.has(movieKey)) {
      return this.pendingRequests.get(movieKey);
    }

    // Registered BEFORE the cache check is awaited. A poster and a modal
    // asking for the same movie in the same tick used to both get past the
    // check above, because neither had registered yet, and both fetched.
    const moviePromise = (async () => {
      try {
        const cachedData = await getCachedTMDBData(title, year, tmdbId);
        if (cachedData) {
          return extractMovieInfo(cachedData);
        }
      } catch (error) {
        console.warn('Error checking cache for', title, error);
      }

      return new Promise((resolve, reject) => {
        // Add to batch queue
        this.batchQueue.push({ title, year, tmdbId, movieKey, resolve, reject });

        // Schedule batch processing
        this.scheduleBatchProcessing();
      });
    })();

    // Store the promise so duplicate requests can use it, and let go of it
    // once it settles either way (cache hit, fetch, or failure).
    this.pendingRequests.set(movieKey, moviePromise);
    moviePromise.then(
      () => this.pendingRequests.delete(movieKey),
      () => this.pendingRequests.delete(movieKey)
    );

    return moviePromise;
  }

  // Schedule batch processing with debouncing
  scheduleBatchProcessing() {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    this.batchTimeout = setTimeout(() => {
      this.processBatch();
    }, this.batchDelay);
  }

  // Process a batch of movie requests
  async processBatch() {
    if (this.batchQueue.length === 0) return;

    // Take up to maxBatchSize items from the queue
    const batch = this.batchQueue.splice(0, this.maxBatchSize);
    
    console.log(`🎬 Processing TMDB batch: ${batch.length} movies`);

    // Process each movie in the batch
    for (const { title, year, tmdbId, movieKey, resolve, reject } of batch) {
      try {
        // Fetch movie data
        const movieDetails = await findBestMovieMatch(title, year, tmdbId);
        const movieInfo = extractMovieInfo(movieDetails);

        // Cache the result (if caching is enabled)
        try {
          await setCachedTMDBData(title, year, movieDetails, tmdbId);
        } catch (cacheError) {
          console.warn('Failed to cache TMDB data for', title, cacheError);
        }

        resolve(movieInfo);
      } catch (error) {
        console.error(`Error fetching TMDB data for ${title}:`, error);
        reject(error);
      } finally {
        // Clean up the pending request
        this.pendingRequests.delete(movieKey);
      }

      // Add delay between API calls to respect rate limits
      if (batch.indexOf({ title, year, movieKey, resolve, reject }) < batch.length - 1) {
        await new Promise(resolve => setTimeout(resolve, this.apiDelay));
      }
    }

    // If there are more items in the queue, schedule another batch
    if (this.batchQueue.length > 0) {
      this.scheduleBatchProcessing();
    }
  }

  // Pre-load TMDB data for a collection of movies with optimized cache checking
  async preloadMovieCollection(movies) {
    console.log(`🚀 Pre-loading TMDB data for ${movies.length} movies...`);
    
    // Import batch cache checking function
    const { batchCheckCache } = await import('../services/tmdbCache');
    
    // First, do a bulk cache check to avoid individual database requests
    const cacheResults = await batchCheckCache(movies.map(movie => ({
      title: movie.title,
      year: movie.year,
      tmdbId: tmdbIdOf(movie)
    })));

    // Only process movies that aren't fully cached
    const uncachedMovies = [];
    const cachedCount = cacheResults.size;

    for (const movie of movies) {
      const cacheKey = getCacheKey(movie.title, movie.year, tmdbIdOf(movie));
      if (!cacheResults.has(cacheKey)) {
        uncachedMovies.push(movie);
      }
    }

    console.log(`💾 Found ${cachedCount} movies in cache, fetching ${uncachedMovies.length} from TMDB API...`);

    // Process uncached movies with the existing batch system
    if (uncachedMovies.length > 0) {
      const promises = uncachedMovies.map(movie =>
        this.getMovieData(movie.title, movie.year, tmdbIdOf(movie)).catch(error => {
          console.warn(`Failed to preload ${movie.title}:`, error);
          return null;
        })
      );

      try {
        await Promise.all(promises);
      } catch (error) {
        console.error('Error in batch preload:', error);
      }
    }
    
    const totalSuccessful = cachedCount + uncachedMovies.length;
    console.log(`✅ Pre-loaded TMDB data: ${totalSuccessful}/${movies.length} successful`);
    
    return {
      total: movies.length,
      cached: cachedCount,
      fetched: uncachedMovies.length,
      successful: totalSuccessful
    };
  }

  // Clear all pending requests (useful for cleanup)
  clearPendingRequests() {
    this.pendingRequests.clear();
    this.batchQueue = [];
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }
  }

  // Get status information
  getStatus() {
    return {
      pendingRequests: this.pendingRequests.size,
      queueLength: this.batchQueue.length,
      batchScheduled: !!this.batchTimeout
    };
  }
}

// Global instance
const batchTMDBLoader = new BatchTMDBLoader();

// Export the main functions
export const getBatchedTMDBData = (title, year, tmdbId = null) => {
  return batchTMDBLoader.getMovieData(title, year, tmdbId);
};

export const preloadMovieCollection = (movies) => {
  return batchTMDBLoader.preloadMovieCollection(movies);
};

export const getBatchLoaderStatus = () => {
  return batchTMDBLoader.getStatus();
};

export const clearBatchLoader = () => {
  batchTMDBLoader.clearPendingRequests();
};

export default batchTMDBLoader;


